export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "https://news-api.farzeen.stream";

export type Tag = { name: string; slug?: string };
export type PrimarySource = { source_id: string; evidence_id: string; name: string; url?: string | null; first_seen_at?: string | null };
export type EnrichmentState = { job_id: string | null; status: "never_run" | "queued" | "running" | "succeeded" | "partial" | "failed" | "cancelled" | string };
export type Company = { company_id: string; company_version?: number; name: string; official_url?: string | null; country?: string | null; status?: string; description?: string | null; description_origin?: string; offering?: string | null; problem?: string | null; industries?: Tag[]; technologies?: Tag[]; funding_stage?: string | null; first_seen_at?: string; last_checked_at?: string; last_enriched_at?: string | null; confidence_score?: number; external_profile_links?: Record<string, string>; primary_source?: PrimarySource | null; source_count?: number; enrichment?: EnrichmentState };
export type Event = { event_id: string; kind: string; title: string; occurred_at?: string; amount?: string | null; currency?: string | null; confidence?: string; evidence_url?: string };
export type Evidence = { evidence_id: string; claim: string; source_name: string; source_url: string; source_type?: string; verified?: boolean; published_at?: string };
export type Source = { source_id: string; name: string; state: string; last_successful_scan_at?: string; coverage?: string; health?: string; items_last_run?: number };
export type Stats = { companies?: number; discoveries_24h?: number; updates_7d?: number; sources_healthy?: number };
export type Page<T> = { items: T[]; next_cursor?: string | null };
export type ReviewItem = { review_id: string; kind: string; title: string; status: string; created_at?: string };
export type ReviewDecision = "accepted" | "rejected";
export type Capabilities = Record<string, boolean>;
export type SessionInfo = { authenticated: boolean; email?: string; role?: string; capabilities?: Capabilities };
export type EnrichmentOverlayItem = { company_id: string; company_version: number | null; enrichment: EnrichmentState & { can_generate: boolean } };
export type EnrichmentJob = {
  job_id: string;
  company_id: string;
  status: string;
  stage: string;
  mode: "fill_missing" | "refresh_ai";
  created_at: string;
  started_at?: string | null;
  finished_at?: string | null;
  applied_fields: string[];
  suggested_fields: string[];
  rejected_fields: string[];
  failure_category?: string | null;
  failure_code?: string | null;
  company_version_at_start: number;
  company_version_now?: number | null;
};

export function cleanText(value: unknown): string { return typeof value === "string" ? value.replace(/[\u0000-\u001f\u007f]/g, " ").trim() : ""; }
export function normaliseCompany(raw: Record<string, unknown>): Company {
  const enrichment = raw.enrichment as Record<string, unknown> | undefined;
  return {
    company_id: cleanText(raw.company_id) || cleanText(raw.id),
    company_version: typeof raw.company_version === "number" ? raw.company_version : undefined,
    name: cleanText(raw.name),
    official_url: cleanText(raw.official_url || raw.website_url) || null,
    country: cleanText(raw.country) || null,
    status: cleanText(raw.status),
    description: cleanText(raw.description) || null,
    description_origin: cleanText(raw.description_origin),
    offering: cleanText(raw.offering) || null,
    problem: cleanText(raw.problem) || null,
    industries: Array.isArray(raw.industries) ? raw.industries as Tag[] : [],
    technologies: Array.isArray(raw.technologies) ? raw.technologies as Tag[] : [],
    funding_stage: cleanText(raw.funding_stage) || null,
    first_seen_at: cleanText(raw.first_seen_at),
    last_checked_at: cleanText(raw.last_checked_at),
    last_enriched_at: cleanText(raw.last_enriched_at) || null,
    confidence_score: typeof raw.confidence_score === "number" ? raw.confidence_score : undefined,
    external_profile_links: raw.external_profile_links as Record<string, string> | undefined,
    primary_source: raw.primary_source as PrimarySource | null | undefined,
    source_count: typeof raw.source_count === "number" ? raw.source_count : undefined,
    enrichment: enrichment ? { job_id: cleanText(enrichment.job_id) || null, status: cleanText(enrichment.status) || "never_run" } : undefined,
  };
}
export function safeExternalUrl(value: unknown): string | undefined { if (typeof value !== "string") return undefined; try { const parsed = new URL(value); return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.href : undefined; } catch { return undefined; } }
export function problemDetail(value: unknown): string { if (typeof value === "string") return cleanText(value); if (value && typeof value === "object" && "detail" in value) return problemDetail((value as { detail: unknown }).detail); return ""; }
export class ApiError extends Error { constructor(public status: number, message: string, public code?: string, public requestId?: string) { super(message); } }
async function parse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail = "The service could not complete this request.";
    let code: string | undefined;
    let requestId: string | undefined;
    try {
      const body = await response.json();
      if (body && typeof body === "object") {
        detail = problemDetail(body) || detail;
        code = cleanText((body as { code?: unknown }).code) || undefined;
        requestId = cleanText((body as { request_id?: unknown }).request_id) || undefined;
      }
    } catch { /* safe fallback */ }
    throw new ApiError(response.status, detail, code, requestId);
  }
  return response.status === 204 ? undefined as T : response.json() as Promise<T>;
}
async function get<T>(path: string): Promise<T> { return parse<T>(await fetch(`${API_BASE_URL}${path}`, { credentials: "include", headers: { Accept: "application/json" } })); }
export async function post<TResponse, TBody>(path: string, body: TBody, headers?: Record<string, string>): Promise<TResponse> {
  return parse<TResponse>(await fetch(`${API_BASE_URL}${path}`, { method: "POST", credentials: "include", headers: { Accept: "application/json", "Content-Type": "application/json", ...headers }, body: JSON.stringify(body) }));
}

export const API_QUERY_KEYS = ["q", "sort", "industry", "tech", "stage", "country", "date_basis", "date_from", "date_to"] as const;
export type CompanyQuery = { q?: string; sort?: string; industry?: string; tech?: string; stage?: string; country?: string; date_basis?: string; date_from?: string; date_to?: string };
export function serialiseCompanyQuery(state: CompanyQuery): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of API_QUERY_KEYS) {
    const value = state[key];
    if (value) params.set(key, value);
  }
  return params;
}
export function parseCompanyQuery(search: string): CompanyQuery {
  const params = new URLSearchParams(search);
  const state: CompanyQuery = {};
  for (const key of API_QUERY_KEYS) {
    const value = params.get(key);
    if (value) state[key] = value;
  }
  return state;
}

export const api = {
  stats: () => get<Stats>("/v1/stats"),
  companies: (query: CompanyQuery, cursor?: string) => {
    const params = serialiseCompanyQuery(query);
    if (cursor) params.set("cursor", cursor);
    return get<Page<Record<string, unknown>>>(`/v1/companies?${params}`).then(page => ({ ...page, items: page.items.map(normaliseCompany) }));
  },
  company: (id: string) => get<Record<string, unknown>>(`/v1/companies/${encodeURIComponent(id)}`).then(normaliseCompany),
  events: (id: string) => get<Page<Event>>(`/v1/companies/${encodeURIComponent(id)}/events`),
  evidence: (id: string) => get<Page<Evidence>>(`/v1/companies/${encodeURIComponent(id)}/evidence`),
  updates: () => get<Page<Record<string, unknown>>>("/v1/updates").then(page => ({ ...page, items: page.items.map(normaliseCompany) })),
  facets: () => get<Record<string, string[]>>("/v1/facets"),
  sources: () => get<Page<Source>>("/v1/sources"),
  review: () => get<Page<ReviewItem>>("/admin/review"),
  decideReview: (review_id: string, decision: ReviewDecision, reason: string) => post<{ status: string }, { review_id: string; decision: ReviewDecision; reason: string }>("/admin/review", { review_id, decision, reason }),
  session: () => get<SessionInfo>("/admin/session"),
  enrichmentState: (companyIds: string[]) => get<{ items: EnrichmentOverlayItem[] }>(`/admin/companies/enrichment-state?company_ids=${encodeURIComponent(companyIds.join(","))}`),
  createEnrichmentJob: (companyId: string, mode: "fill_missing" | "refresh_ai", expectedCompanyVersion: number | undefined, idempotencyKey: string) => post<{ job_id: string; status: string; duplicated: boolean; mode: string }, { mode: string; reason: string; expected_company_version?: number }>(
    `/admin/companies/${encodeURIComponent(companyId)}/enrichment-jobs`,
    { mode, reason: "Generate missing company description and metadata", expected_company_version: expectedCompanyVersion },
    { "Idempotency-Key": idempotencyKey }
  ),
  enrichmentJob: (jobId: string) => get<EnrichmentJob>(`/admin/ai-enrichment/jobs/${encodeURIComponent(jobId)}`),
};
